import NavBase from "./lib/nav_base";
import { PanesManager } from "./lib/panes_manager";
import Tracker from "../../lib/tracker";
import {NativeAppSignalEmitter} from "../../lib/native_app_signal_emitter";
import {getSharedSessionStateDetector, SESSION_RECHECK_EVENT} from "../../lib/session_state";
import { getTrackerOptions } from "../../lib/tracker_helpers";
import {
    getCurrentPaneOptionComponent,
    getPaneOptionComponentByIdentifier,
} from "./instances/state_machine/actions/helpers/pane_option_helpers";
import { primaryPaneOptionCollection } from "./instances/primary_pane_option_collection";
import {NavToggleComponent} from "./components/nav_toggle_component";
import {Version} from "../lib/version";
import {
    NAV_TOGGLE_REQUESTED_EVENT,
    NAV_OPEN_REQUESTED_EVENT,
    NAV_CLOSE_REQUESTED_EVENT,
} from "../../constants/nav_events";

export const NavConfig = {
    bootedEventName: "OnlineAppNav-booted",
    // Retired as the primary contract by the sole-owner rework (plan 2026-07-15), but still
    // EMITTED to legacy field apps below the version floor — see `usesLegacyNavContract()`.
    toggledEventName: "OnlineAppNav-toggled",
    openEventName: "OnlineAppNav-open",
    closeEventName: "OnlineAppNav-close",
    configuredEventName: "OnlineAppNav-configured",
};

export class Nav extends NavBase {

    // Native apps at/above these versions implement the sole-owner "reflector" contract
    // (the `-*Requested` inbound vocabulary + reflecting our `-open`/`-close`), shipped in the
    // 2026-07-15 rework. BELOW these floors the field apps predate that contract: they own the
    // drawer themselves and never tell the webview when a native control opens/closes it, so the
    // webview's authoritative `navOpen` would drift and swallow the first toggle. For those apps
    // we fall back to the pre-rework fire-and-forget behaviour (see `usesLegacyNavContract()`).
    // Web hosts (non-native) always use the new model. iOS 2.24.0 is the first release carrying
    // the reflector (develop reused 2.23.6 = the broken field build, so the floor MUST be higher).
    static MIN_IOS_VERSION = "2.24.0";
    static MIN_ANDROID_VERSION = "3.36.1";

    constructor(navRoot, props) {
        super(props);

        this.navRoot = navRoot;

        this._untrackedSelection = false; // Set while the nav selects something itself — see `withUntrackedSelection()`.
        this.bootPaneId = undefined;          // The pane the nav booted on, before restoring the last selection.
        this.paneOpenBaselineId = undefined;  // Set while NAV_PANE_OPENED is judged against another pane — see `withPaneOpenBaseline()`.

        this.code = props.code;

        this.defaultMapFilterCode = props.default_map_filter_code; // The map filter to use when there have been no previously selected map filters (no persisted state).
        this.defaultNavOptionIdentifier = props.default_nav_option_identifier; // The nav option to use when the app is first loaded (no persisted state).
        this.freshInstallNavOpen = props.fresh_install_nav_open; // Explicit fresh-install nav state (boolean; undefined/null = fall back to the pane instruction). Decision 8, plan 2026-07-28.

        this.isNativeApp = props.hasOwnProperty("is_native_app")
            ? props["is_native_app"]
            : null;

        this.nativeAppPlatform = props.hasOwnProperty("native_app_platform")
            ? props["native_app_platform"]
            : null;

        this.nativeAppVersion = Version.new(props["native_app_version"]);

        // The `NativeAppSignalEmitter` is used to communicate to the native apps via signals.
        if (props["native_app_signal_emitter_options"]) this.nativeAppSignalEmitter = new NativeAppSignalEmitter(props["native_app_signal_emitter_options"]);

        // Initialise the site-wide session-state detector here — the nav is loaded
        // on every online-app page and already owns the native signal emitter, so
        // this is the page-load hook that catches EVERY login-state change (OTP
        // verify, password login, rankometer / ferry-discount auto-login, logout,
        // silent expiry) and emits Person-loggedIn/-loggedOut to native. AccountPanel
        // subscribes to this same shared detector for its own rendering.
        if (props["session_state_endpoint"]) {
            this.sessionStateDetector = getSharedSessionStateDetector({
                endpointUrl: props["session_state_endpoint"],
                signalEmitter: this.nativeAppSignalEmitter, // undefined on web → emission no-ops
            });
            this.sessionStateDetector.check();

            // Cross-surface session sync (plans/2026-07-09--cross-surface-session-sync.md).
            // Two orthogonal global re-check triggers, surface-agnostic:
            //
            // 1. Native fan-out inbound: when any surface logs in/out, native pokes
            //    EVERY live webview with the payload-free SESSION_RECHECK_EVENT. We
            //    re-read the cookie truth with emitToNative:false so this stays
            //    one-way (native → surfaces) with no echo back to native.
            window.addEventListener(SESSION_RECHECK_EVENT, () =>
                this.sessionStateDetector.check({ emitToNative: false }),
            );
            // 2. Visibility/pageshow backstop: catches eventless changes (silent
            //    60-day expiry) and old-app degradation (no native fan-out) by
            //    re-checking on refocus. Default emitToNative:true — a genuine
            //    detection here should propagate to native like any other.
            document.addEventListener("visibilitychange", () => {
                if (document.visibilityState === "visible") this.sessionStateDetector.check();
            });
            window.addEventListener("pageshow", () => this.sessionStateDetector.check());
        }

        // Inbound nav-intent requests (plan 2026-07-15). The Nav webview is the sole owner of nav
        // open/closed state; hosts (the native drawer + the web online_app) REQUEST a change via
        // these `window` events and then reflect the OnlineAppNav-open/-close we emit back. One
        // listener, two dispatchers: native broadcasts via WebViewRegistry; the web online_app
        // dispatches the same names as a same-document CustomEvent. No echo — the "-*Requested"
        // names are distinct from our outbound "-open"/"-close", so emitting can't retrigger this.
        window.addEventListener(NAV_TOGGLE_REQUESTED_EVENT, () => this.toggleNav());
        window.addEventListener(NAV_OPEN_REQUESTED_EVENT, () => this.openNav());
        window.addEventListener(NAV_CLOSE_REQUESTED_EVENT, () => this.closeNav());

        this.stateMachine.setPersistentStorageKey(
            `${this.code}__${this.stateMachine.persistentStorageKey}`,
        ); // We want a unique store for each online app.

        this.tracker = new Tracker(
            this.win,
            this.doc,
            getTrackerOptions(props.tracker_options),
        );

        this.navToggles = getNavToggleComponents(navRoot.element, this);

        // Initialize the panes manager and this constructs all the panes and pane options.
        this.panesManager = PanesManager.new(this);

        this.boot();

        // Fire a "booted" event to signal that this Nav has completed initialization.
        // The OnlineApp can listen and then complete it's boot process.
        document.dispatchEvent(new CustomEvent(NavConfig.bootedEventName, { detail: this }));
    }

    boot() {
        this.dispatchAction("bootingStarted");
        this.setInitialState(); // Restores the persisted navOpen (among other state) into the SM.

        // Legacy field apps (below the version floor) predate the sole-owner model: the Nav does
        // NOT own open/closed state there, so there's no persisted-wins suppression and no
        // authoritative boot emit. The initial pane option's nav-toggle instruction drives the
        // boot open/close exactly as it did pre-rework (via the unconditional openNav/closeNav).
        if (this.usesLegacyNavContract()) {
            this.renderInitial();
            this.dispatchAction("bootingEnded");
            return;
        }

        // Persisted nav-open state wins over the initial pane option's nav-toggle instruction
        // (plan 2026-07-15). On a fresh install (no persisted navOpen), an app-configured
        // `fresh_install_nav_open` states the first-run intent directly (Decision 8, plan
        // 2026-07-28). Before it, first-run state was an accident of two mechanisms: the state
        // machine's initial `navOpen: true` (state.js) made the boot emit report OPEN, and on
        // native the default option's pane instruction additionally drove openNav — nobody ever
        // decided "first run opens the nav". The default is persisted like any other state write,
        // so the next launch restores it via the persisted-wins path. Apps that declare no
        // default keep the old fallthrough.
        const hasPersistedNavOpen = typeof this.stateMachine.getStoreItem("navOpen") !== "undefined";
        const applyFreshInstallNavDefault = !hasPersistedNavOpen && typeof this.freshInstallNavOpen === "boolean";
        if (applyFreshInstallNavDefault) this.setNavOpen(this.freshInstallNavOpen);
        this._suppressNavInstruction = hasPersistedNavOpen || applyFreshInstallNavDefault;

        this.renderInitial();

        this._suppressNavInstruction = false;
        this.dispatchAction("bootingEnded");

        // Authoritatively emit the final open/closed state so the host reflects it on launch.
        this.emitInitialNavOpenState();
    }

    // Emit the current navOpen as the outbound open/close signal, for the host to reflect on launch.
    // Emits UNCONDITIONALLY (not via the idempotent openNav/closeNav): even when navOpen hasn't
    // "changed" from its default, the host still needs to be told the state to sync its drawer.
    emitInitialNavOpenState() {
        this.emitNavSignal(this.readNavOpen() ? NavConfig.openEventName : NavConfig.closeEventName);
    }

    setInitialState() {
        // Restore any state that might be persisted in the browser storage.
        this.dispatchAction("initFromPersistentStore");
    }

    renderInitial() {
        // Restoring where the person left off is the nav's own selection, not theirs, so it isn't
        // counted (plans/2026-09-24--nav-programmatic-selections-counted.md). The click still fires:
        // it is how the native apps and the OnlineApp learn the selection.
        this.withUntrackedSelection(() => {
            // Ensure the current pane is set and rendered.
            const pane = this.panesManager?.defaultPane
            if (pane) this.dispatchAction("openPane", pane);
            this.bootPaneId = pane?.identifier; // The pane before the restore — see `withPaneOpenBaseline()`.

            // Ensure the current pane option selected. This might open a secondary pane and change the current pane.
            const paneOptionComponent = this.getInitialPaneOptionComponent();
            if (paneOptionComponent) paneOptionComponent.fireClickEventOnLink();
        });
    }

    // Run `fn` as a selection the nav makes itself (boot restore, the pane-close fallback), so the
    // NAV_OPTION_SELECTED / NAV_PANE_OPENED it would send are skipped. The synthetic click dispatches
    // synchronously, so the flag covers exactly that selection. Restores the previous value, so a
    // nested call can't clear an outer one early.
    withUntrackedSelection(fn) {
        const wasUntracked = this._untrackedSelection;
        this._untrackedSelection = true;
        try {
            return fn();
        } finally {
            this._untrackedSelection = wasUntracked;
        }
    }

    // Is the current selection one the nav is making itself? Selection trackers skip sending while it is.
    isUntrackedSelection() {
        return !!this._untrackedSelection;
    }

    // Run `fn` with NAV_PANE_OPENED judged against `paneId` rather than the current pane (see
    // `openPaneComponent`). The initial deep link uses the pane the nav booted on: the boot restore may
    // have switched to another pane the person never saw, and the deep link's count shouldn't depend
    // on that. Restores the previous value, like `withUntrackedSelection()`.
    withPaneOpenBaseline(paneId, fn) {
        const previous = this.paneOpenBaselineId;
        this.paneOpenBaselineId = paneId;
        try {
            return fn();
        } finally {
            this.paneOpenBaselineId = previous;
        }
    }

    rankersAnalyticsEventTracker(eventName, options) {
        if (!this.tracker || !this.tracker.eventTracker) return;
        console.log(
            `[Nav] rankersAnalyticsEventTracker(): eventName: ${eventName} `,
            options,
        );
        this.tracker.eventTracker(eventName, options);
    }

    getRootElement() {
        return this.getNavRoot()?.element;
    }

    getNavRoot() {
        return this.navRoot;
    }

    // Returns the pane option component for selection on boot.
    // The order of precedence is:
    //  - current option (from state)
    //  - the default option
    //  - fall back to the first in the collection
    getInitialPaneOptionComponent() {
        return (
            getCurrentPaneOptionComponent(this.stateMachine) ||
            getPaneOptionComponentByIdentifier(this.defaultNavOptionIdentifier) ||
            primaryPaneOptionCollection[0]
        );
    }

    // Returns a map filter code (which identifies a pane option).
    // Either the default map filter code or the first map filter code in the collection.
    getInitialMapFilterCode() {
        return (
            getPaneOptionComponentByIdentifier(this.defaultMapFilterCode)
                ?.mapFilterCode ||
            primaryPaneOptionCollection.find(
                (paneOption) => !!paneOption.mapFilterCode,
            )?.mapFilterCode
        );
    }

    // The Nav webview is the SOLE owner of open/closed state (plan 2026-07-15). These methods are
    // the one place that mutates it: they persist `navOpen` and then emit the outbound open/close
    // signal for the host (native drawer or web online_app) to reflect. They are called by the
    // nav's own controls (NavToggleComponent), by boot, and by the inbound "-*Requested" listener.

    // Toggle resolves to open/close from the persisted value, so a host that only knows "toggle"
    // (a hamburger/menu button) gets deterministic, persisted behaviour.
    // Legacy field apps below the floor never update our `navOpen` (they own the drawer natively),
    // so resolving from it misfires the first tap. There we emit the pre-rework fire-and-forget
    // `-toggled` signal and let the app act against its OWN drawer state, as it always has.
    toggleNav() {
        if (this.usesLegacyNavContract()) {
            this.emitNavSignal(NavConfig.toggledEventName);
            return;
        }
        this.readNavOpen() ? this.closeNav() : this.openNav();
    }

    openNav() {
        // Legacy: emit unconditionally, no persist/idempotency (the Nav doesn't own state there).
        if (this.usesLegacyNavContract()) {
            this.emitNavSignal(NavConfig.openEventName);
            return;
        }
        if (this.readNavOpen() === true) return; // Already open — no-op, no duplicate emit.
        this.setNavOpen(true);
        this.emitNavSignal(NavConfig.openEventName);
    }

    closeNav() {
        // Legacy: emit unconditionally, no persist/idempotency (the Nav doesn't own state there).
        if (this.usesLegacyNavContract()) {
            this.emitNavSignal(NavConfig.closeEventName);
            return;
        }
        if (this.readNavOpen() === false) return; // Already closed — no-op, no duplicate emit.
        this.setNavOpen(false);
        this.emitNavSignal(NavConfig.closeEventName);
    }

    // Is this host a native app OLDER than the sole-owner "reflector" contract (plan 2026-07-15)?
    // Those field apps don't participate in the `-*Requested` vocabulary and never report their
    // native-driven drawer changes back, so the Nav must not treat its `navOpen` as authoritative
    // for them — it falls back to the pre-rework fire-and-forget signalling instead. Fail-safe:
    // web hosts and at/above-floor native apps use the new model; anything native we can't
    // positively confirm as at/above-floor (unknown version or platform) gets the legacy path,
    // which is exactly the behaviour running in production today — safe for every field build.
    usesLegacyNavContract() {
        if (!this.isNativeApp) return false;         // web online_app host → new sole-owner model
        if (!this.nativeAppVersion) return true;     // native but unparseable version → safe legacy
        if (this.nativeAppPlatform === "ios") return this.nativeAppVersion.isLessThan(Nav.MIN_IOS_VERSION);
        if (this.nativeAppPlatform === "android") return this.nativeAppVersion.isLessThan(Nav.MIN_ANDROID_VERSION);
        return true;                                 // native, unknown platform → safe legacy
    }

    // Emit the outbound open/close signal to whichever host we're in: the native app (via the
    // signal-URL emitter) or the web online_app (via a same-document CustomEvent it reflects).
    emitNavSignal(eventName) {
        if (this.isNativeApp && this.nativeAppSignalEmitter) {
            this.nativeAppSignalEmitter.emit(eventName);
        } else {
            document.dispatchEvent(new CustomEvent(eventName, { detail: this }));
        }
    }

    // The persisted open/closed value (nav state machine). `setNavOpen` no-ops when unchanged.
    readNavOpen() {
        return !!this.readState("navOpen");
    }

    // Opening the nav is when its options become visible, so it also counts the impressions withheld
    // while it was shut (plans/2026-09-23--nav-impressions-drawer-blind.md).
    setNavOpen(open) {
        const wasOpen = this.readNavOpen();
        this.dispatchAction("setNavOpen", open);
        if (!wasOpen && this.readNavOpen()) this.flushPendingImpressions();
    }

    // Count the current pane's nav option impressions that were withheld while the nav was shut and
    // are still in view. Only the current (focused) pane: every other pane has stopped observing.
    flushPendingImpressions() {
        const pane = this.panesManager?.getCurrentPane();
        if (pane && pane.flushPendingPaneOptionViews) pane.flushPendingPaneOptionViews();
    }

}

function getNavToggleComponents(parentElement, nav) {
    if (!parentElement) return [];
    return Array.from(parentElement.querySelectorAll(NavToggleComponent.config.selector)).map((element) => {
        return NavToggleComponent.new(element, nav);
    });
}

